import React, { useState, useContext } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AppContext, OPEN_TOAST } from "../Context";
import { makeStyles } from "@material-ui/styles";
import { Button } from "@mantine/core";
import ListItem from "@material-ui/core/ListItem";
import Typography from "@material-ui/core/Typography";
import LocationIcon from "@material-ui/icons/Place";
import PaymentIcon from "@material-ui/icons/Payment";
import TimeIcon from "@material-ui/icons/Schedule";
import { MY_BOOKINGS } from "../routes";
import BookButton from "./bookButton";
import Progress from "../Common/progress";
import ActivityColor from "../Common/activityColor";
import SideInfo from "../Common/sideInfo";
import { STATE_ACTIVE_PERSON_ID, STATE_CULTURE } from "../Actions/stateActions";
import { ModalContext } from "../Context/ModalContext";
import { getDuration } from "../Common/util";

const useStyles = makeStyles((theme) => ({
  listItem: {
    border: `1px solid ${theme.app.iconDisabledColor}`,
    borderBottom: "none",
    background: theme.app.background[1],
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gridGap: "8px",
    alignItems: "center",
    width: "100%",
    padding: "0.5rem",
    "&:first-child": {
      borderRadius: "4px 4px 0 0",
    },
    "&:last-child": {
      borderRadius: "0 0 4px 4px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
    "&:only-child": {
      borderRadius: "4px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
    "@media(max-width: 1300px)": {
      "&:first-child": {
        borderRadius: 0,
      },
      "&:last-child": {
        borderRadius: 0,
        borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
      },
      "&:only-child": {
        borderRadius: 0,
        borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
      },
    },
    "@media(max-width: 600px)": {
      "&:first-child": {
        marginTop: "16px",
      },
      gridTemplateColumns: "1fr",
      marginBottom: "16px",
      borderBottom: `1px solid ${theme.app.iconDisabledColor}`,
    },
  },
  content: { padding: "8px" },
  infoContainer: {
    display: "grid",
    gridTemplateColumns: "auto auto auto 1fr",
    gridGap: "16px",
    alignItems: "center",
    margin: "8px 0 0",
    "@media(max-width: 600px)": {
      gridGap: "8px",
    },
  },
  title: {
    "@media(max-width: 667px)": {
      fontSize: "1.1rem",
    },
  },
  resource: {
    color: theme.app.labelColor,
    lineHeight: 1,
    fontSize: "0.875rem",
  },
  info: {},
  infoIcon: {
    fontSize: "20px",
    color: theme.app.labelColor,
  },
  infoItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    color: theme.app.labelColor,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  infoText: {
    color: theme.app.labelColor,
    fontSize: "0.8125rem",
    fontWeight: 500,
    "&:before": {
      content: '" "',
      whiteSpace: "pre",
    },
    "@media(max-width: 600px)": {
      fontSize: "0.75rem",
    },
  },
  reason: {
    fontStyle: "italic",
    color: theme.app.dangerColor,
    lineHeight: 1.6,
    fontSize: "0.875rem",
  },
  actionContainer: {
    height: "100%",
    display: "flex",
    alignItems: "flex-end",
    padding: "8px",
    "@media(max-width: 600px)": {
      padding: "4px 0",
      // borderTop: `1px solid ${theme.app.borderColor}`,
      justifyContent: "flex-end",
    },
  },
  actionButton: {
    marginRight: "8px",
    color: theme.app.labelColor,
    "@media(max-width: 600px)": {
      marginRight: 0,
      border: "none",
    },
  },
  actionButtonText: {},
}));

const BOOK_OCCASION = gql`
  mutation reserveOccasion($id: String, $participantId: Guid) {
    reserveOccasion(occasionId: $id, participantId: $participantId) {
      position
      failed
      errorMessage
    }
  }
`;

const getState = (state) => ({
  activePersonId: state.cookies[STATE_ACTIVE_PERSON_ID],
  culture: state.cookies[STATE_CULTURE],
  language: state.language,
});

const Occasion = React.memo(function Occasion(props) {
  const { occasion, manages } = props;
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const context = useContext(AppContext);
  const { openModal, setOpenModal } = useContext(ModalContext);
  const { dispatch } = context;
  const { culture, language, activePersonId } = useSelector((state) =>
    getState(state)
  );
  const handleShowInfo = () => setOpen(true);
  const handleCloseInfo = () => setOpen(false);
  const handleMutationSuccess = ({ reserveOccasion }) => {
    const { failed, errorMessage } = reserveOccasion;

    if (failed) {
      return dispatch({
        type: OPEN_TOAST,
        toastText: errorMessage,
        toastType: "error",
      });
    }
    if (price.inclVat > 0) {
      setOpenModal(true);
    } else {
      dispatch({
        type: OPEN_TOAST,
        toastText: language.bookingSucceed,
        toastType: "success",
      });
    }
  };
  const handleMutationError = (error) => {
    dispatch({
      type: OPEN_TOAST,
      toastText: language.errorMsg,
      ToastType: "error",
    });
  };
  const [bookOccasion, { loading }] = useMutation(BOOK_OCCASION, {
    variables: { id: occasion.id, participantId: activePersonId },
    onCompleted: handleMutationSuccess,
    onError: handleMutationError,
    refetchQueries: () => [{ query: MY_BOOKINGS }],
  });
  const activeLocale = culture.split("-")[0];
  const activity = occasion.activity;
  const occasionDuration = getDuration(
    occasion.duration,
    activeLocale,
    language
  );
  const price = occasion.activity.price || {};
  const booked = Math.round(
    ((occasion.maxParticipants - occasion.freeSlots) /
      occasion.maxParticipants) *
      100
  );
  const anyResource = occasion.resourceAssignments.length > 0;
  const resource = anyResource && occasion.resourceAssignments[0].resource;

  const blocked = !occasion.canReserve.reserve && !occasion.canReserve.enqueue;
  const reason =
    language.reason[occasion.canReserve.reason] || occasion.canReserve.reason;

  return (
    <>
      <ListItem className={classes.listItem}>
        <div className={classes.content}>
          <Typography variant="h3" className={classes.title}>
            {activity.name}
          </Typography>
          <Typography className={classes.resource}>{`${
            resource.name ? resource.name : ""
          }`}</Typography>
          {blocked && (
            <Typography className={classes.reason} component="div">
              {reason}
            </Typography>
          )}
          <div className={classes.infoContainer}>
            <ActivityColor color={activity.color} />
            <Progress booked={booked} />
            <div className={classes.info}>
              <Typography component="div" className={classes.infoItem}>
                <LocationIcon className={classes.infoIcon} />
                <div className={classes.infoText}>{activity.location.name}</div>
              </Typography>
              <Typography component="div" className={classes.infoItem}>
                <PaymentIcon className={classes.infoIcon} />
                <div className={classes.infoText}>
                  {price.inclVat > 0 ? `${price.inclVat}:-` : `0:-`}
                </div>
              </Typography>
            </div>
            <div className={classes.info}>
              <Typography component="div" className={classes.infoItem}>
                <TimeIcon className={classes.infoIcon} />
                <div className={classes.infoText}>
                  {moment(occasion.at).format("LT")}
                </div>
              </Typography>
              <Typography component="div" className={classes.infoItem}>
                <TimeIcon className={classes.infoIcon} />
                <div className={classes.infoText}>{occasionDuration}</div>
              </Typography>
            </div>
          </div>
        </div>
        <div className={classes.actionContainer}>
          <Button
            size="sm"
            color="violet.8"
            variant="outline"
            className={classes.actionButton}
            onClick={handleShowInfo}
            aria-label={`${language.infoAboutActivity} ${activity.name}, ${
              language.occasion_time
            } ${moment(occasion.at).format("HH:mm dddd DD MMMM YYYY")}`}
          >
            <div className={classes.actionButtonText}>{language.showInfo}</div>
          </Button>
          {!blocked && (
            <BookButton
              loading={loading}
              bookingData={occasion}
              book={bookOccasion}
              manages={manages}
            />
          )}
        </div>
      </ListItem>
      <SideInfo
        open={open}
        onClose={handleCloseInfo}
        activity={activity}
        occasion={occasion}
      />
    </>
  );
});

export default Occasion;
